class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.freeShippingReached = Boolean(this.querySelector('.cart-drawer__shipping--reached'));
  }

  connectedCallback() {
    this.cartUpdateUnsubscriber?.();
    this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, ({ source }) => {
      if (source === 'cart-items') this.syncFreeShippingCelebration();
    });
  }

  disconnectedCallback() {
    this.cartUpdateUnsubscriber?.();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
      this.replayShippingProgress();
      this.syncFreeShippingCelebration();
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn =
          (this.classList.contains('is-empty') ? this.querySelector('.drawer__inner-empty') : null) ||
          document.getElementById('CartDrawer') ||
          this.querySelector('.drawer__inner');
        if (!containerToTrapFocusOn) return;
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    /* themeScrollLock بيعمل position: fixed كمان — overflow: hidden
       لوحدها مش بتوقف تمرير الصفحة على iOS Safari. */
    if (window.themeScrollLock) window.themeScrollLock.lock();
    else document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent();
  }

  /*
    Re-runs the free-shipping bar from 0 to whatever Liquid rendered.

    The width is a server-rendered inline --fs-progress, so on its own the bar
    only animates when the value actually changes — open the drawer twice with
    the same cart and it just sits there. Snapping to 0 and handing the target
    back on the next frame turns every open into a fresh fill, without the
    server value ever being lost: it is read off the element first and written
    straight back, so the element stays the single source of truth and repeated
    replays can't drift.

    This drives the replay with an explicit animation rather than by poking
    --fs-progress, because the CSS transition fights that approach twice over:
    writing 0% doesn't snap the bar, it starts a transition *towards* 0, and
    suppressing the transition to force the snap then leaves it inactive on
    the frame the target goes back on — so the bar lands on its final width
    with no travel either way.

    An animation sidesteps both. It outranks the transition while it runs and
    reverts cleanly when it ends, so --fs-progress is never written at all and
    the server-rendered value stays untouched no matter how often this runs.
    Timing is read back from the stylesheet so the CSS stays the one place
    duration and easing are defined.
  */
  replayShippingProgress() {
    const fill = this.querySelector('.cart-drawer__shipping-fill');
    if (!fill || typeof fill.animate !== 'function') return;

    const target = fill.style.getPropertyValue('--fs-progress');
    if (!target || parseFloat(target) === 0) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const styles = getComputedStyle(fill);
    const duration = (parseFloat(styles.transitionDuration) || 0.76) * 1000;

    fill.animate(
      [{ inlineSize: '0%' }, { inlineSize: target }],
      { duration, easing: styles.transitionTimingFunction || 'ease-out' },
    );
  }

  syncFreeShippingCelebration() {
    const reached = Boolean(this.querySelector('.cart-drawer__shipping--reached'));
    const newlyReached = reached && !this.freeShippingReached;
    this.freeShippingReached = reached;
    if (!newlyReached || !this.classList.contains('active')) return;
    if (document.visibilityState === 'hidden' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    this.celebrateFreeShipping();
  }

  celebrateFreeShipping() {
    const panel = this.querySelector('.drawer__inner');
    const shipping = this.querySelector('.cart-drawer__shipping');
    if (!panel || !shipping) return;

    this.querySelector('.cart-drawer__confetti')?.remove();
    const layer = document.createElement('div');
    layer.className = 'cart-drawer__confetti';
    layer.setAttribute('aria-hidden', 'true');
    const panelRect = panel.getBoundingClientRect();
    const shippingRect = shipping.getBoundingClientRect();
    const originY = Math.min(Math.max(shippingRect.bottom - panelRect.top - 10, 64), panel.clientHeight * 0.45);
    layer.style.setProperty('--confetti-origin-y', `${originY}px`);

    const colors = ['#e53935', '#ffca28', '#26a69a', '#42a5f5', '#ab47bc', '#ff7043'];
    const fragment = document.createDocumentFragment();
    const spread = Math.min(panel.clientWidth * 0.44, 180);
    for (let index = 0; index < 28; index += 1) {
      const piece = document.createElement('span');
      const endX = (Math.random() * 2 - 1) * spread;
      const turn = (Math.random() < 0.5 ? -1 : 1) * (240 + Math.random() * 540);
      piece.className = 'cart-drawer__confetti-piece';
      piece.style.backgroundColor = colors[index % colors.length];
      piece.style.borderRadius = index % 4 === 0 ? '50%' : '1px';
      piece.style.setProperty('--confetti-mid-x', `${endX * 0.65}px`);
      piece.style.setProperty('--confetti-rise', `${-(55 + Math.random() * 90)}px`);
      piece.style.setProperty('--confetti-end-x', `${endX}px`);
      piece.style.setProperty('--confetti-fall', `${190 + Math.random() * 240}px`);
      piece.style.setProperty('--confetti-mid-turn', `${turn * 0.45}deg`);
      piece.style.setProperty('--confetti-turn', `${turn}deg`);
      piece.style.setProperty('--confetti-duration', `${1250 + Math.random() * 500}ms`);
      piece.style.setProperty('--confetti-delay', `${Math.random() * 150}ms`);
      fragment.appendChild(piece);
    }
    layer.appendChild(fragment);
    panel.appendChild(layer);
    setTimeout(() => layer.remove(), 2000);
  }

  close() {
    this.classList.remove('active');
    this.querySelector('.cart-drawer__confetti')?.remove();
    removeTrapFocus(this.activeElement);
    if (window.themeScrollLock) window.themeScrollLock.unlock();
    else document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      // open() bails out when the drawer is already showing, which is exactly
      // the add-to-cart-from-an-open-drawer case — the one where the bar most
      // needs to move. Replay it here for that path only; when the drawer was
      // closed, open() does it.
      const wasOpen = this.classList.contains('active');
      this.open();
      if (wasOpen) {
        this.replayShippingProgress();
        this.syncFreeShippingCelebration();
      }
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
